import { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { flatten } from 'lodash';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { Button } from 'components/common/button';
import { getDifferenceInDays, formatDate } from 'lib';
import {
    dateFormatStr,
    dateFormatV3Str,
    MAX_SEARCH_NIGHTS,
} from './../../constants';
import {
    setCalendarAlert,
    setCheckInDay,
    setCheckOutDay,
    setTempCheckOutDay,
} from 'features/userSlice';
import { useGetBookingData, useGetCalendarDay } from 'hooks';

import { LowestRatesSlider } from 'components/lowestRatesSlider';
import { Month } from './Month';
import './BookingCalendar.css';

const BookingCalendar = ({
    currentMonthIndex,
    setCurrentMonthIndex,
    mobile,
    onDatesClick,
    datesOptions,
    setCalendarOpen,
    variant = 'default',
}) => {
    const dispatch = useDispatch();
    const isOpen = useSelector((state) => state.user?.isCalendarOpen);
    const Lang = useSelector((state) => state.miniBookingWidget?.data?.Lang);

    const { getBookingData } = useGetBookingData();
    const selectedPropertyId = useSelector(
        (state) => state.multiproperty?.selectedPropertyId
    );

    const calendar = useSelector(
        (state) => state.miniBookingWidget?.data?.futureDates
    );

    const weekdaysInitials = useSelector(
        (state) => state.miniBookingWidget?.data?.DateInitials
    );

    const userCountryCode =
        useSelector((state) => state.user?.language) || 'en-US';

    let userCheckInDay = useSelector((state) => state.user?.checkInDay);

    if (onDatesClick) {
        userCheckInDay = useGetCalendarDay(datesOptions?.checkin);
    }

    let tempCheckInDate = useSelector((state) => state.user?.checkInDay?.date);
    let tempCheckOutDate = useSelector(
        (state) => state.user?.checkOutDay?.date
    );
    if (datesOptions) {
        tempCheckInDate = datesOptions.checkin;
        tempCheckOutDate = datesOptions.checkout;
    }

    const tempCheckOutDay = useSelector((state) => state.user?.tempCheckOutDay);

    const calendarAlert = useSelector((state) => state.user?.calendarAlert);

    const nights = getDifferenceInDays(
        tempCheckOutDay?.date || tempCheckOutDate,
        tempCheckInDate
    );

    const calendarContentsRef = useRef(null);

    const currentMonth = calendar[currentMonthIndex];
    const nextMonth = calendar[currentMonthIndex + 1];

    const handleArrowClick = (direction) => {
        if (direction === 'L') {
            setCurrentMonthIndex(
                currentMonthIndex > 0
                    ? currentMonthIndex - 1
                    : currentMonthIndex
            );

            // TODO: TRY ANIMATING TRANSITION
        }

        if (direction === 'R') {
            setCurrentMonthIndex(
                currentMonthIndex + 2 === calendar?.length
                    ? currentMonthIndex
                    : currentMonthIndex + 1
            );
        }
    };

    const handleLowestRateClick = useCallback(
        (day, nextDay, monthIndex) => {
            setCurrentMonthIndex(monthIndex);
            dispatch(setCheckInDay({ day }));
            if (nextDay) {
                dispatch(setCheckOutDay({ day: nextDay }));
            }

            // Rest temp check out day
            dispatch(setTempCheckOutDay({ day: null }));
        },
        [calendar]
    );

    const [lowestRateDays, lowestRateNextDays, lowestRateMonthIndexes] =
        useMemo(() => {
            const filteredNextDays = [];
            const filteredMonthIndexes = [];

            const filteredDays = calendar?.map((month, monthIndex) => {
                return month?.fd_JsonResult?.data?.days?.filter(
                    (day, index, daysArr) => {
                        if (day?.isLowest) {
                            const nextDay = daysArr[index + 1];
                            filteredMonthIndexes.push(monthIndex);
                            filteredNextDays.push(nextDay);
                            return day;
                        }

                        return false;
                    }
                );
            });

            return [
                flatten(filteredDays),
                filteredNextDays,
                filteredMonthIndexes,
            ];
        }, [calendar]);

    useEffect(() => {
        const minStay = userCheckInDay?.min_stay;

        if (nights > MAX_SEARCH_NIGHTS) {
            dispatch(
                setCalendarAlert({ msg: Lang?.maxSearchNightsAllowedMsg })
            );
        } else if (userCheckInDay?.min_stay > 1 && nights < minStay) {
            const msg =
                minStay > 1
                    ? `${Lang?.minStayMsg} ${minStay} ${Lang?.nights}`
                    : `${Lang?.minStayMsg} ${minStay} ${Lang?.night}`;
            dispatch(setCalendarAlert({ msg }));
        }
    }, [isOpen]);

    if (!tempCheckInDate || !tempCheckOutDate) {
        return null;
    }
    if (variant === 'singleMonth') {
        const headerTitle =
            Lang?.findTheBestPricesForYourNextTrip ||
            'Find the best prices for your next trip';

        return (
            currentMonth && (
                <div className="ab-calendar ab-calendar--single">
                    <div className="ab-calendar-single-header">
                        <span className="ab-calendar-single-title">
                            {headerTitle}
                        </span>
                        <button
                            type="button"
                            className="ab-calendar-single-close"
                            onClick={() => setCalendarOpen(false)}
                        >
                            ×
                        </button>
                    </div>

                    <div className="ab-calendar-single-month-strip ab-custom-scrollbar">
                        {calendar?.map((month, index) => {
                            const year = month?.fd_Year;
                            const monthIndex = (month?.fd_Month || 1) - 1;

                            const monthName =
                                year != null
                                    ? new Date(
                                          year,
                                          monthIndex,
                                          1
                                      ).toLocaleString('en-US', {
                                          month: 'short',
                                      })
                                    : (month?.MonthText || '').split(' ')[0];

                            return (
                                <button
                                    key={`tab-${month?.fd_Year}-${month?.fd_Month}`}
                                    type="button"
                                    className={
                                        index === currentMonthIndex
                                            ? 'ab-calendar-single-month-tab ab-calendar-single-month-tab--active'
                                            : 'ab-calendar-single-month-tab'
                                    }
                                    onClick={() => setCurrentMonthIndex(index)}
                                >
                                    <span className="ab-calendar-single-month">
                                        {monthName}
                                    </span>
                                    <span className="ab-calendar-single-year">
                                        {year}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* <div className="ab-calendar-single-slider">
                        <input
                            type="range"
                            min={0}
                            max={Math.max((calendar?.length || 1) - 1, 0)}
                            value={currentMonthIndex}
                            onChange={(e) =>
                                setCurrentMonthIndex(Number(e.target.value))
                            }
                        />
                    </div> */}

                    <div className="ab-calendar-single-month-wrapper">
                        <Month
                            days={currentMonth?.fd_JsonResult?.data?.days}
                            mobile={false}
                            monthNo={currentMonth?.fd_Month}
                            monthText={currentMonth?.MonthText}
                            weekdaysInitials={weekdaysInitials}
                            key={`calendar-month-table-${currentMonth?.fd_Year}-${currentMonth?.fd_Month}`}
                            onDatesClick={onDatesClick}
                            datesOptions={datesOptions}
                            setCalendarOpen={setCalendarOpen}
                            // variant="symbolFirst"
                        />
                    </div>

                    <div className="ab-calendar-single-footer">
                        <div className="ab-calendar-single-footer-left">
                            {nights > 0 && userCheckInDay && (
                                <>
                                    <div className="ab-calendar-single-price">
                                        {userCheckInDay?.currency_symbol}
                                        {userCheckInDay?.price}
                                    </div>
                                    <div className="ab-calendar-single-meta">
                                        {`${Lang?.avgPerNight || 'avg/night'} ${
                                            Lang?.forTxt || 'for'
                                        } ${nights} ${
                                            nights === 1
                                                ? Lang?.night || 'night'
                                                : Lang?.nights || 'nights'
                                        }`}
                                    </div>
                                    <div className="ab-calendar-single-dates">
                                        {tempCheckInDate &&
                                            formatDate(
                                                new Date(tempCheckInDate),
                                                dateFormatV3Str,
                                                userCountryCode
                                            )}{' '}
                                        –{' '}
                                        {formatDate(
                                            new Date(
                                                tempCheckOutDay?.date ||
                                                    tempCheckOutDate
                                            ),
                                            dateFormatV3Str,
                                            userCountryCode
                                        )}
                                    </div>
                                </>
                            )}

                            {Boolean(calendarAlert) && (
                                <div className="ab-calendar-alert">
                                    {calendarAlert}
                                </div>
                            )}
                        </div>

                        <div className="ab-calendar-single-footer-right">
                            <Button
                                color="#fff"
                                onClick={() => {
                                    if (onDatesClick) {
                                        setCalendarOpen(false);
                                    } else {
                                        getBookingData({
                                            propertyId: selectedPropertyId,
                                        });
                                    }
                                }}
                            >
                                {Lang?.chooseRoom || 'Choose Room'}
                            </Button>
                            <Button
                                color="#fff"
                                variant="ghost"
                                onClick={() => setCalendarOpen(false)}
                            >
                                {Lang?.cancel || 'Cancel'}
                            </Button>
                        </div>
                    </div>
                </div>
            )
        );
    }

    return (
        currentMonth && (
            <div
                className={`ab-calendar ${mobile ? 'ab-calendar--mobile' : ''}`}
            >
                <div className="ab-calendar-months ab-custom-scrollbar">
                    {!mobile && (
                        <div
                            role="button"
                            className={`iHotelWebAppCalendarMonthArrows iHotelWebAppCalendarMonthArrowPrevious ab-calendar-control-btn ${
                                currentMonthIndex === 0
                                    ? 'ab-inactive-arrow-btn'
                                    : ''
                            }`}
                            onClick={() => handleArrowClick('L')}
                        >
                            <FaChevronLeft />
                        </div>
                    )}

                    <div className="ab-dropdown-calendar-inner">
                        <div className="iHotelWebAppCalendar iHotelWebAppCalendarWrapper">
                            <div
                                id="iHotelWebAppCalendarContents"
                                ref={calendarContentsRef}
                            >
                                {mobile ? (
                                    <div className="iHotelWebAppCalendarTableDiv">
                                        {calendar?.map((month) => {
                                            return (
                                                <Month
                                                    days={
                                                        month?.fd_JsonResult
                                                            ?.data?.days
                                                    }
                                                    mobile={mobile}
                                                    monthNo={month?.fd_Month}
                                                    monthText={month?.MonthText}
                                                    weekdaysInitials={
                                                        weekdaysInitials
                                                    }
                                                    key={`calendar-month-table-${month?.fd_Year}-${month?.fd_Month}`}
                                                    onDatesClick={onDatesClick}
                                                    datesOptions={datesOptions}
                                                    setCalendarOpen={
                                                        setCalendarOpen
                                                    }
                                                />
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="iHotelWebAppCalendarTableDiv">
                                        <Month
                                            days={
                                                currentMonth?.fd_JsonResult
                                                    ?.data?.days
                                            }
                                            mobile={mobile}
                                            monthNo={currentMonth?.fd_Month}
                                            monthText={currentMonth?.MonthText}
                                            weekdaysInitials={weekdaysInitials}
                                            key={`calendar-month-table-${currentMonth?.fd_Year}-${currentMonth?.fd_Month}`}
                                            onDatesClick={onDatesClick}
                                            datesOptions={datesOptions}
                                            setCalendarOpen={setCalendarOpen}
                                        />
                                        <Month
                                            days={
                                                nextMonth?.fd_JsonResult?.data
                                                    ?.days
                                            }
                                            mobile={mobile}
                                            monthNo={nextMonth?.fd_Month}
                                            monthText={nextMonth?.MonthText}
                                            weekdaysInitials={weekdaysInitials}
                                            key={`calendar-month-table-${nextMonth?.fd_Year}-${nextMonth?.fd_Month}`}
                                            onDatesClick={onDatesClick}
                                            setCalendarOpen={setCalendarOpen}
                                            datesOptions={datesOptions}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {!mobile && (
                        <div
                            role="button"
                            className={`iHotelWebAppCalendarMonthArrows iHotelWebAppCalendarMonthArrowNext ab-calendar-control-btn ${
                                currentMonthIndex === calendar?.length - 2
                                    ? 'ab-inactive-arrow-btn'
                                    : ''
                            }`}
                            onClick={() => handleArrowClick('R')}
                        >
                            <FaChevronRight />
                        </div>
                    )}
                </div>

                <div className="ab-calendar-footer">
                    {/* Nights */}
                    <div className="ab-calendar-selected-days">
                        <div>
                            {userCheckInDay
                                ? formatDate(
                                      new Date(userCheckInDay?.date),
                                      dateFormatStr,
                                      userCountryCode
                                  )
                                : formatDate(
                                      new Date(tempCheckInDate),
                                      dateFormatStr,
                                      userCountryCode
                                  )}
                            {' - '}
                            {tempCheckOutDay
                                ? formatDate(
                                      new Date(tempCheckOutDay?.date),
                                      dateFormatStr,
                                      userCountryCode
                                  )
                                : formatDate(
                                      new Date(tempCheckOutDate),
                                      dateFormatStr,
                                      userCountryCode
                                  )}
                            <span> ({`${nights} ${Lang?.nights}`})</span>
                        </div>
                        <div style={{ paddingLeft: '15px' }}>
                            {Lang?.calendarAdults}
                        </div>
                    </div>

                    <div className="ab-calendar-footer-bottom">
                        <div className="ab-priceLegend">
                            <div className="ab-priceRange ab-low">
                                {Lang?.available}
                            </div>
                            <div className="ab-priceRange ab-medium">
                                {Lang?.minStay}
                            </div>
                            <div className="ab-priceRange ab-high">
                                {Lang?.soldOut}
                            </div>
                            <div className="ab-priceRange ab-min-price">
                                {Lang?.minPrice}
                            </div>
                        </div>
                        {Boolean(calendarAlert) && (
                            <div className="ab-calendar-alert">
                                {calendarAlert}
                            </div>
                        )}

                        {/* TODO: CHANGE THIS - PASS IT AS A PROP */}
                        {mobile && (
                            <Button
                                color="#fff"
                                onClick={() => {
                                    if (onDatesClick) {
                                        setCalendarOpen(false);
                                    } else {
                                        getBookingData({
                                            propertyId: selectedPropertyId,
                                        });
                                    }
                                }}
                                styles={{
                                    'background-color': 'rgb(0, 66, 126)',
                                    'margin-bottom': '15px',
                                    'margin-top': '15px',
                                    padding: '25px',
                                }}
                                width="100%"
                            >
                                {Lang?.selectDates}
                            </Button>
                        )}
                    </div>

                    {!mobile && (
                        <LowestRatesSlider
                            lowestRateDays={lowestRateDays}
                            lowestRateNextDays={lowestRateNextDays}
                            lowestRateMonthIndexes={lowestRateMonthIndexes}
                            Lang={Lang}
                            onClick={handleLowestRateClick}
                            countryCode={userCountryCode}
                            title={Lang?.lowestMonthlyRate}
                        />
                    )}
                </div>
            </div>
        )
    );
};

BookingCalendar.propTypes = {
    currentMonthIndex: PropTypes.number,
    setCurrentMonthIndex: PropTypes.func,
    mobile: PropTypes.bool,
    onDatesClick: PropTypes.func,
    setCalendarOpen: PropTypes.func,
    datesOptions: PropTypes.object,
    variant: PropTypes.oneOf(['default', 'singleMonth']),
};

export default BookingCalendar;
